import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import ProfilePanel from '~/components/common/user-profile/ProfilePanel';
import ProfileTabs from '~/components/common/user-profile/ProfileTabs';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import {
  ProfileTab,
  ProfileTabsCollection,
  getTabIndex,
} from '~/lib/util/profile.util';
import { Path } from '~/routes/constants';

import ProviderProfileHeader from './ProviderProfileHeader';
import { useProviderProfileData } from '~/lib/hooks/useProviderProfileData';

const ProfileOverview = () => {
  const { mode } = useModeContext();
  const isAdmin = mode === UserMode.ADMIN;
  const params = useParams<UserProfileParams>();
  const providerExternalId = params.id ?? params[0].split('/')[0];

  const { providerData, avatarUrl, isLoading } = useProviderProfileData(
    providerExternalId,
    isAdmin,
  );

  const providerCredentials = useMemo(
    () => providerData?.credentialsDetails.map((item) => item.code),
    [providerData],
  );

  const providerPronouns = useMemo(
    () => providerData?.pronounsDetails.map((item) => item.name),
    [providerData],
  );
  const providerFirstName =
    providerData?.displayFirstName || providerData?.firstName;
  const providerMiddleName =
    providerData?.displayMiddleName || providerData?.middleName;
  const providerLastName =
    providerData?.displayLastName || providerData?.lastName;
  const providerSuffix = providerData?.displaySuffix || providerData?.suffix;

  let basePath = `${Path.ADMIN_USERS}/${params.id}/profile`;
  if (mode === UserMode.MAUD) {
    basePath = `${Path.VIEW_PROFILE}/${params[0].split('/')[0]}`;
  } else if (mode === UserMode.ADMIN) {
    basePath = `${Path.ADMIN_VIEW_PROFILE}/${params.id}`;
  } else if (mode === UserMode.OPS) {
    basePath = `${Path.OPS_VIEW_PROFILE}/${params.id}`;
  }

  return (
    <>
      <ProviderProfileHeader
        avatarUrl={avatarUrl}
        credentials={providerCredentials}
        firstName={providerFirstName}
        isLoading={isLoading}
        lastName={providerLastName}
        middleName={providerMiddleName}
        otherPronounText={providerData?.otherPronounText}
        pronouns={providerPronouns}
        providerTypeName={providerData?.providerTypeDetails?.name}
        stateOfResidence={providerData?.stateOfResidenceDetails?.name}
        suffix={providerSuffix}
      />

      <ProfileTabs<ProfileTab>
        basePath={basePath}
        defaultPath={ProfileTabsCollection[ProfileTab.PROFILE]?.subPath}
        getTabIndex={getTabIndex}
        isLoading={isLoading}
        Panels={ProfilePanel}
        tabsDictionary={ProfileTabsCollection}
        title="UserProfile"
      />
    </>
  );
};

export default ProfileOverview;
